import { useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import styled from '@emotion/styled';
import {
  Button,
  PageHeader,
  tokens,
  Loader,
  Stack,
  Icon,
  Text,
  Alert,
  Tooltip,
} from '@hotjar/hotsauce';
import { useServiceProducts } from '@hotjar/service-management';
import { useStickyNotification } from '@hotjar/navigation';
import { useTranslationWithPrefix, useHelpHotjarUrl } from '@hotjar/translations';
import { useAccountTrialInfo } from '@hotjar/account';
import { useQuery } from 'react-query';
import { usePageTitle, useTrack } from '../external-dependencies/webapp-utils';
import {
  OrganizationSummary,
  useServiceResourceList,
} from '~/subscription-management/features/service-resource-list';
import { PriceReview } from '../features/price-review/PriceReview';
import { CHECKOUT } from '~/subscription-management/constants/routes';
import { useSubscriptionInfo } from '../queries';
import { usePrettifyPrice } from '../utils/cost/prettify-price';
import { mapServiceProductToProductAllowance } from '../utils/mappers';
import { useOrderStatus } from '../queries/useOrderStatus';
import { PAGE, SUBSCRIPTIONS_HEADER } from '../utils/tracking';
import { FlatCard, ManageSubscriptionButton } from '../components';
import {
  getTrialRemainingDays,
  isOnActiveTrial,
  isOnEligibleTrial,
} from '../utils/trial/trial.utils';
import { ChurnBanner } from '../components/churn-banner/ChurnBanner';
import { prettifyDate } from '../utils/date.utils';
import { getDraftOrder } from '../draft-order';
import { getAccountDiscounts } from '../utils/discounts';
import { CONTACT_SUPPORT_LINK } from '../constants/links';
import { UpdateInvoiceDetailsBanner } from '../components/page-skeleton/UpdateInvoiceDetailsBanner';
import ManuallyManagedSvg from '../resources/images/manually-managed.svg';

const S = {
  HeightContainer: styled.div`
    height: calc(100vh - 56px);
    background: ${tokens.BRAND_COLORS.neutral_100};
  `,
  FlexContainer: styled.div`
    display: flex;
    padding-top: ${tokens.spacer.x3};
    justify-content: center;
    background: ${tokens.BRAND_COLORS.neutral_100};
  `,
  ContentContainer: styled.div`
    width: min(100%, 786px);
  `,
  Stack: styled(Stack)`
    margin-top: ${tokens.spacer.x3};
    margin-bottom: ${tokens.spacer.x3};
  `,
  Card: styled(FlatCard)`
    background-color: transparent;
    flex-direction: row;
    align-items: center;
    gap: ${tokens.spacer.x2};
    padding-inline: ${tokens.spacer.x3};
  `,
  Illustration: styled.img`
    width: 100px;
  `,
};

const ORDER_STATUS_REFETCH_INTERVAL = 2 * 1000;

export function Subscriptions(): JSX.Element {
  const [t, tPageTitle, tAlerts] = useTranslationWithPrefix([
    'checkout:subscriptions',
    'checkout:page-title',
    'checkout:alerts',
  ]);
  const { data: subscriptionInfo, isFetched: isSubscriptionFetched } = useSubscriptionInfo();
  const { customer, user } = subscriptionInfo || {};
  const { data: draftOrderData } = useQuery('draft-order', getDraftOrder);
  const { data: productAllowances } = useServiceProducts(user?.accountId);
  const { click: trackClick } = useTrack();
  const navigate = useNavigate();
  const trialInfo = useAccountTrialInfo();
  const { hide: hideNotification } = useStickyNotification();
  const helpHotjarUrl = useHelpHotjarUrl();

  const currency = customer?.currency ?? user?.currency ?? 'EUR';
  const { prettifyPrice } = usePrettifyPrice(currency);
  const billingPeriod = customer?.subscription.billingPeriod ?? 'MONTHLY';
  const hasActiveSubscription = customer?.subscription.status === 'ACTIVE';
  const hasCancelledSubscription = customer?.subscription.status === 'CANCELLED';
  const nextBillingDate = customer?.subscription.currentPeriodEndDate;

  const {
    data: orderStatus,
    isLoading: isOrderStatusLoading,
    isFetched,
  } = useOrderStatus(user?.accountId, ORDER_STATUS_REFETCH_INTERVAL);

  const { allResources, loading } = useServiceResourceList({
    userId: user?.userId as number,
    accountId: user?.accountId as number,
    productAllowances: {
      effectiveNow: productAllowances?.effectiveNow.map(mapServiceProductToProductAllowance) ?? [],
      renewWith: productAllowances?.renewWith.map(mapServiceProductToProductAllowance) ?? [],
    },
    billingPeriod,
    currency,
    costs: {
      current: customer?.currentPeriodCostSummary,
      future: customer?.currentPeriodCostSummary,
    },
  });

  /**
   * NOTE: we need to check hasActiveSubscription because of a bug in the backend where a
   * cancelled subscription still returns the active costs in its currentPeriodCostSummary
   * * TODO: remove when https://hotjar.atlassian.net/browse/MP-2122 is done
   */
  const price = (hasActiveSubscription && customer?.currentPeriodCostSummary.amountWithTax) || 0;
  const accountDiscounts = getAccountDiscounts(
    customer?.currentPeriodCostSummary?.purchasedProductCosts,
    false,
    customer?.subscription,
  );

  const isOrderProcessing = orderStatus?.status === 'PROCESSING';
  const hasOrderFailed = orderStatus?.status === 'FAILED';
  const isExternalPaymentCustomer =
    customer?.paymentMethod.paymentMethodType === 'EXTERNAL_PAYMENT_METHOD';

  const nextChargeDate =
    customer?.subscription.currentPeriodEndDate !== undefined
      ? prettifyDate(customer?.subscription.currentPeriodEndDate, user?.country)
      : undefined;

  const trackLearnAboutProductsClick = () =>
    trackClick(SUBSCRIPTIONS_HEADER.externalPricing, PAGE.subscriptions, {
      account_id: user?.accountId,
    });

  const trackManageSubscriptionsClick = () =>
    trackClick(SUBSCRIPTIONS_HEADER.manageSubscriptions, PAGE.subscriptions, {
      account_id: user?.accountId,
    });

  const handleManageSubscriptionClick = () => {
    trackManageSubscriptionsClick();
    navigate(CHECKOUT);
  };

  const getOnTrialAlertMessage = (): string => {
    if (isOnActiveTrial(trialInfo)) {
      return t('active-trial-alert', { days: getTrialRemainingDays(trialInfo) });
    }

    if (isOnEligibleTrial(trialInfo)) {
      return t('eligible-trial-alert');
    }

    return '';
  };

  usePageTitle(tPageTitle('subscriptions'));

  /**
   * We're hiding here a global notification that can be triggered by Angular subscription service
   * This page itself handles displaying notification about the trail which is ending
   * /packages/legacy/src/webapp-ng/src/app/js/services/billing/v2/subscription.js → showAlerts
   */
  useEffect(() => {
    hideNotification('subscription-issue');
  }, [hideNotification]);

  return (
    <S.HeightContainer>
      <UpdateInvoiceDetailsBanner billingAddress={customer?.billingAddress} />
      <S.FlexContainer>
        <S.ContentContainer>
          <PageHeader
            title={t('title')}
            actions={
              <>
                <Button
                  kind="secondary"
                  isExternal
                  as="a"
                  href="https://www.hotjar.com/pricing/"
                  onClick={trackLearnAboutProductsClick}
                >
                  {t('learn-about-products')}
                </Button>
                {isExternalPaymentCustomer ? (
                  <Tooltip text={tAlerts('external-payments.tooltip')}>
                    <Button as="a" href={`${helpHotjarUrl}${CONTACT_SUPPORT_LINK}`} isExternal>
                      {tAlerts('external-payments.contact-support')}
                    </Button>
                  </Tooltip>
                ) : (
                  <ManageSubscriptionButton
                    onClick={handleManageSubscriptionClick}
                    isDisabled={!isFetched || isOrderProcessing || isOrderStatusLoading}
                  >
                    {t('manage-subscriptions')}
                  </ManageSubscriptionButton>
                )}
              </>
            }
          />

          <S.Stack direction="column" spacing="x3">
            {trialInfo?.activeOrEligible === true && <Alert>{getOnTrialAlertMessage()}</Alert>}

            {isExternalPaymentCustomer && (
              <S.Card>
                <S.Illustration src={ManuallyManagedSvg} alt="" />
                <Stack direction="column" spacing="x0pt5">
                  <Text textType="subtitle2">{tAlerts('external-payments.title')}</Text>
                  <Text>{tAlerts('external-payments.description')}</Text>
                </Stack>
              </S.Card>
            )}

            {isOrderProcessing && (
              <Alert title={t('payment-processing-warning.title')} kind="warning">
                {t('payment-processing-warning.description')}
              </Alert>
            )}

            {!isExternalPaymentCustomer && draftOrderData?.products && (
              <Alert
                kind="success"
                actionText={tAlerts('pre-selected-plans.continue')}
                onAction={handleManageSubscriptionClick}
              >
                {tAlerts('pre-selected-plans.other-changes-only')}
              </Alert>
            )}

            {hasOrderFailed && (
              <Alert title={tAlerts('order-failures.title')} kind="danger">
                {tAlerts('order-failures.description')}
              </Alert>
            )}

            {hasCancelledSubscription && (
              <ChurnBanner
                disableManageSubscription={false}
                nextBillingDate={nextBillingDate ?? ''}
              />
            )}

            <PriceReview loading={loading}>
              <PriceReview.Price
                loading={loading}
                title={t('cost.title')}
                price={t(`cost.price-${billingPeriod.toLowerCase()}`, {
                  price: prettifyPrice(price),
                })}
              />
              <PriceReview.ExtraInfo
                subtitle={
                  hasActiveSubscription
                    ? t('cost.next-charge-date', {
                        date: nextChargeDate,
                      })
                    : ''
                }
              />
              <PriceReview.AccountDiscount
                accountDiscounts={accountDiscounts}
                currency={currency}
                loading={loading}
              />
              {!hasActiveSubscription && isSubscriptionFetched && (
                <Text textType="caption" textColor="text_light">
                  <Icon icon="tag" /> {t('cost.save-on-yearly', { percentage: 20 })}
                </Text>
              )}
            </PriceReview>

            <Loader size="xl" isLoading={loading}>
              {allResources?.map((organization) => (
                <OrganizationSummary
                  organization={organization}
                  key={organization.id}
                  billingPeriod={billingPeriod}
                  currency={currency}
                  editable="HIDDEN"
                  subscriptionEndDate={nextChargeDate}
                  showBundleDiscount={!isExternalPaymentCustomer && !isOrderStatusLoading}
                />
              ))}
            </Loader>
          </S.Stack>
        </S.ContentContainer>
      </S.FlexContainer>
    </S.HeightContainer>
  );
}
